import {TextField} from '@common/ui/forms/input-field/text-field/text-field';
import {LandingPageContent} from '@app/landing/landing-page-content';
import {Button} from '@common/ui/buttons/button';
import {useTrans} from '@common/i18n/use-trans';
import {useMutation} from '@tanstack/react-query';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';
import {apiClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {useMemo, useRef, useState} from 'react';
import {urlIsValid} from '@app/dashboard/links/utils/url-is-valid';
import {Link as LinkModel} from '@app/dashboard/links/link';
import {Trans} from '@common/i18n/trans';
import {flushSync} from 'react-dom';
import {useSettings} from '@common/core/settings/use-settings';
import {useRecaptcha} from '@common/recaptcha/use-recaptcha';
import {useLightThemeVariables} from '@common/ui/themes/use-light-theme-variables';
import {useAuth} from '@common/auth/use-auth';
import {Link as RouterLink} from 'react-router-dom';

interface LandingPageNewLinkFormProps {
  content: LandingPageContent;
}

function buildQrUrl(shortUrl: string): string {
  return shortUrl ? `${String(shortUrl).replace(/\/+$/, '')}/qr` : '';
}

function safeFilename(input: string) {
  return String(input || 'qr')
    .trim()
    .toLowerCase()
    .replace(/https?:\/\//g, '')
    .replace(/[^\w\-]+/g, '-')
    .replace(/\-+/g, '-')
    .replace(/^\-|\-$/g, '');
}

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '');
}

export function LandingPageNewLinkForm({content}: LandingPageNewLinkFormProps) {
  const {trans} = useTrans();
  const lightThemeVars = useLightThemeVariables();
  const {verify, isVerifying} = useRecaptcha('link_creation');
  const {
    links: {default_type},
  } = useSettings();

  const {hasPermission} = useAuth();
  const isGuest = !hasPermission('links.view');

  const inputRef = useRef<HTMLInputElement>(null);

  const [longUrl, setLongUrl] = useState('');
  const [shortUrl, setShortUrl] = useState('');

  // QR panel button states
  const [copyShortState, setCopyShortState] = useState<
    'idle' | 'copied' | 'error'
  >('idle');
  const [copyQrState, setCopyQrState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );
  const [downloadQrState, setDownloadQrState] = useState<
    'idle' | 'downloading' | 'done'
  >('idle');

  // Copy QR image state (desktop only feature)
  const [copyQrImgState, setCopyQrImgState] = useState<
    'idle' | 'copying' | 'copied' | 'error'
  >('idle');
  const [qrFlash, setQrFlash] = useState(false);

  const qrUrl = useMemo(() => buildQrUrl(shortUrl), [shortUrl]);

  // copy mode = have shortUrl and user is not typing a new longUrl
  const isCopyMode = !!shortUrl && !longUrl;

  const createPayload: Partial<LinkModel> = {
    long_url: longUrl,
    type: default_type || 'direct',
  };

  const mobile = useMemo(() => isMobileDevice(), []);

  function triggerQrFlash(success: boolean) {
    setQrFlash(true);
    window.setTimeout(() => setQrFlash(false), 650);
    if (!success) {
      // keep subtle, no forced toast here
    }
  }

  async function copyShortUrl() {
    if (!shortUrl) return;
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopyShortState('copied');
      window.setTimeout(() => setCopyShortState('idle'), 1400);
      toast.positive(message('Copied link to clipboard'));
    } catch {
      setCopyShortState('error');
      window.setTimeout(() => setCopyShortState('idle'), 1600);
      toast.danger(message('Could not copy to clipboard'));
    }
  }

  async function copyQrUrl() {
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopyQrState('copied');
      window.setTimeout(() => setCopyQrState('idle'), 1400);
    } catch {
      setCopyQrState('error');
      window.setTimeout(() => setCopyQrState('idle'), 1600);
    }
  }

  // Desktop-only: copy QR image to clipboard (PNG/SVG depending on server response)
  async function copyQrImageToClipboard() {
    if (!qrUrl) return;

    // Do not attempt on mobile (not reliable)
    if (mobile) return;

    if (copyQrImgState === 'copying') return;

    setCopyQrImgState('copying');
    try {
      const res = await fetch(qrUrl, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const type = (blob.type || 'image/png').toLowerCase();

      // ClipboardItem not supported everywhere
      // @ts-ignore
      if (!window.ClipboardItem || !navigator.clipboard?.write) {
        throw new Error('Clipboard image write not supported');
      }

      const mime = type.startsWith('image/') ? type : 'image/png';

      // @ts-ignore
      const item = new ClipboardItem({[mime]: blob});
      // @ts-ignore
      await navigator.clipboard.write([item]);

      setCopyQrImgState('copied');
      triggerQrFlash(true);
      window.setTimeout(() => setCopyQrImgState('idle'), 1400);
    } catch {
      setCopyQrImgState('error');
      triggerQrFlash(false);
      window.setTimeout(() => setCopyQrImgState('idle'), 1600);

      // Fallback: copy QR URL instead
      try {
        await navigator.clipboard.writeText(qrUrl);
        toast.positive(
          message('Copied QR URL instead (image copy not supported).'),
        );
      } catch {
        toast.danger(message('Could not copy QR image.'));
      }
    }
  }

  async function downloadQrImage() {
    if (!qrUrl) return;

    setDownloadQrState('downloading');
    try {
      const res = await fetch(qrUrl, {credentials: 'include'});
      if (!res.ok) throw new Error();

      const blob = await res.blob();
      const type = (blob.type || '').toLowerCase();
      const ext = type.includes('svg')
        ? 'svg'
        : type.includes('jpg') || type.includes('jpeg')
        ? 'jpg'
        : 'png';

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${safeFilename(shortUrl)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);

      setDownloadQrState('done');
      window.setTimeout(() => setDownloadQrState('idle'), 1200);
    } catch {
      setDownloadQrState('idle');
      window.open(qrUrl, '_blank', 'noopener,noreferrer');
    }
  }

  const copyShortLabel =
    copyShortState === 'copied'
      ? 'Copied!'
      : copyShortState === 'error'
      ? 'Copy failed'
      : 'Copy short URL';

  const copyQrLabel =
    copyQrState === 'copied'
      ? 'Copied!'
      : copyQrState === 'error'
      ? 'Copy failed'
      : 'Copy QR URL';

  const downloadQrLabel =
    downloadQrState === 'downloading'
      ? 'Downloading...'
      : downloadQrState === 'done'
      ? 'Downloaded'
      : 'Download QR';

  const createLink = useMutation({
    mutationFn: () => postCreateLink(createPayload),
    onSuccess: response => {
      toast.positive(trans(message('Link shortened')));
      flushSync(() => {
        setShortUrl(response.link.short_url);
        setLongUrl('');
      });
      inputRef.current?.select();

      // reset button states
      setCopyShortState('idle');
      setCopyQrState('idle');
      setCopyQrImgState('idle');
      setDownloadQrState('idle');
      setQrFlash(false);
    },
    onError: err =>
      showHttpErrorToast(
        err,
        message('Could not shorten link. Please try again later'),
        'long_url',
      ),
  });

  function resetForm() {
    setShortUrl('');
    setLongUrl('');
    setCopyShortState('idle');
    setCopyQrState('idle');
    setCopyQrImgState('idle');
    setDownloadQrState('idle');
    setQrFlash(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  // submit button progress
  const isSubmitting = createLink.isPending || isVerifying;
  const submitLabel = isSubmitting
    ? 'Working...'
    : shortUrl
    ? 'Copy'
    : String(content.actions.inputButton || 'Shorten');

  return (
    <>
      <form
        className="mt-60 w-full md:mt-80"
        onSubmit={async e => {
          e.preventDefault();
          if (isSubmitting) return;

          // Copy mode: copy short link, keep QR visible
          if (isCopyMode && shortUrl) {
            await copyShortUrl();
            return;
          }

          // Validate: must be a valid URL
          if (!urlIsValid(longUrl)) {
            toast.danger(message('Please enter a valid URL.'));
            return;
          }

          const ok = await verify();
          if (ok) createLink.mutate();
        }}
      >
        <TextField
          inputRef={inputRef}
          background="bg-white"
          inputRadius="rounded-full"
          style={lightThemeVars}
          size="lg"
          placeholder={trans(message(content.actions.inputText))}
          value={isCopyMode ? shortUrl : longUrl}
          onChange={e => {
            const v = e.target.value;
            if (isCopyMode && shortUrl) setShortUrl('');
            setLongUrl(v);
          }}
          endAppend={
            <Button
              radius="rounded-r-full"
              type="submit"
              variant="flat"
              color="primary"
              disabled={isSubmitting}
              className="
                min-w-160
                border-2 border-white
                disabled:border-white
                disabled:text-white
                disabled:opacity-100
              "
            >
              <Trans message={submitLabel} />
            </Button>
          }
        />
      </form>

      {/* QR container untouched (layout stays) */}
      {shortUrl ? (
        <div className="mt-18 flex w-full justify-center">
          <div className="w-full max-w-[720px] rounded-2xl border border-white/30 bg-white/15 p-24 shadow-lg backdrop-blur-md">
            <div className="flex justify-center">
              <Button
                type="button"
                variant="flat"
                className="bg-white font-semibold text-primary shadow-md transition hover:bg-white/90 hover:shadow-lg"
                onClick={resetForm}
              >
                <Trans message="New" />
              </Button>
            </div>

            <div className="mt-18 flex justify-center">
              {!mobile ? (
                <button
                  type="button"
                  onClick={copyQrImageToClipboard}
                  className="group relative cursor-pointer rounded-2xl border border-white/20 bg-white p-18 shadow-sm outline-none focus:ring-2 focus:ring-white/40"
                  aria-label="Copy QR image"
                  title="Click to copy QR image"
                >
                  <img
                    src={qrUrl}
                    alt="QR"
                    className={[
                      'h-260 w-260 object-contain transition',
                      qrFlash ? 'scale-[1.02]' : 'scale-100',
                    ].join(' ')}
                    loading="lazy"
                  />

                  <div
                    className={[
                      'pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl transition',
                      qrFlash ? 'bg-black/25 opacity-100' : 'opacity-0',
                    ].join(' ')}
                  >
                    <div className="rounded-full bg-white px-14 py-8 text-sm font-semibold text-black shadow">
                      {copyQrImgState === 'copied'
                        ? 'Copied!'
                        : 'Copy QR image'}
                    </div>
                  </div>
                </button>
              ) : (
                <div className="rounded-2xl border border-white/20 bg-white p-18 shadow-sm">
                  <img
                    src={qrUrl}
                    alt="QR"
                    className="h-260 w-260 object-contain"
                    loading="lazy"
                  />
                </div>
              )}
            </div>

            {/* Buttons row: desktop = 4 buttons, mobile = 3 buttons */}
            <div className="mt-18 flex flex-wrap items-center justify-center gap-12">
              <Button
                variant="outline"
                onClick={copyShortUrl}
                disabled={!shortUrl || copyShortState === 'copied'}
                className="bg-white text-black hover:bg-white/90"
              >
                {copyShortLabel}
              </Button>

              {!mobile ? (
                <Button
                  variant="outline"
                  onClick={copyQrUrl}
                  disabled={!qrUrl || copyQrState === 'copied'}
                  className="bg-white text-black hover:bg-white/90"
                >
                  {copyQrLabel}
                </Button>
              ) : null}

              <Button
                variant="outline"
                onClick={downloadQrImage}
                disabled={!qrUrl || downloadQrState === 'downloading'}
                className="bg-white text-black hover:bg-white/90"
              >
                {downloadQrLabel}
              </Button>

              <Button
                variant="flat"
                color="primary"
                elementType="a"
                href={qrUrl || '#'}
                target="_blank"
                rel="noreferrer"
                disabled={!qrUrl}
              >
                <Trans message="Open QR" />
              </Button>
            </div>

            {/* Upsell under QR */}
            {isGuest ? (
              <div className="mt-18 rounded-2xl border border-white/20 bg-white/10 p-16 text-left text-white backdrop-blur">
                <div className="flex flex-col gap-12 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white/95">
                      <Trans message="Unlock tracking & advanced features" />
                    </div>
                    <div className="mr-6 mt-6 text-xs text-white/80">
                      <Trans message="Unlock all premium features: real-time analytics, smart destinations, biolinks, and more." />
                    </div>

                    <ul className="text-white/85 mt-10 space-y-6 text-xs">
                      <li>
                        •{' '}
                        <Trans message="Real-time click analytics (countries, devices, referrers)" />
                      </li>
                      <li>
                        •{' '}
                        <Trans message="Smart routing by country/device + rotators" />
                      </li>
                      <li>
                        •{' '}
                        <Trans message="Biolinks, campaigns, and editable destinations" />
                      </li>
                    </ul>
                  </div>

                  <div className="flex shrink-0 flex-col gap-10">
                    <Button
                      elementType={RouterLink}
                      to="/register"
                      variant="flat"
                      color="primary"
                      radius="rounded-full"
                      className="min-w-220"
                    >
                      <Trans message="Create free account" />
                    </Button>
                    <Button
                      elementType={RouterLink}
                      to="/login"
                      variant="text"
                      color="paper"
                      radius="rounded-full"
                      className="border-2 border-white text-white/90 hover:bg-white/10"
                    >
                      <Trans message="I already have an account" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

interface CreateLinkResponse extends BackendResponse {
  link: LinkModel;
}
function postCreateLink(
  values: Partial<LinkModel>,
): Promise<CreateLinkResponse> {
  return apiClient.post(`link`, values).then(r => r.data);
}
